import { useEffect, useCallback, useRef } from 'react';
import { useSocket } from '../context/SocketContext';

/**
 * 실시간 협업 훅
 * 기존 상태 변경 함수를 래핑하여 소켓 이벤트를 자동 전송하고,
 * 원격 변경사항을 수신하여 로컬 상태에 적용합니다.
 */
export default function useCollaborativeChart({
  currentFileId,
  tasks,
  setTasks,
  milestones,
  setMilestones,
  users,
  setUsers,
}) {
  const {
    socket,
    // connected is used implicitly via socket events
    currentChartId,
    emitTaskAdd,
    emitTaskUpdate,
    emitTaskDelete,
    emitTaskReorder,
    emitMilestoneAdd,
    emitMilestoneUpdate,
    emitMilestoneDelete,
    emitMemberAdd,
    emitMemberUpdate,
    emitMemberRemove,
    emitSelection,
    requestSync,
    setChartVersion,
  } = useSocket();

  const isRemoteUpdate = useRef(false);

  // ===== 원격 이벤트 수신 =====
  useEffect(() => {
    if (!socket || !currentChartId) return;

    // Task 이벤트
    const onTaskAdded = ({ task, version }) => {
      isRemoteUpdate.current = true;
      setTasks(prev => {
        if (prev.find(t => t.id === task.id)) return prev;
        const newTask = {
          ...task,
          startDate: new Date(task.startDate),
          endDate: new Date(task.endDate),
        };
        return [...prev, newTask];
      });
      setChartVersion(version);
      isRemoteUpdate.current = false;
    };

    const onTaskUpdated = ({ taskId, changes, version }) => {
      isRemoteUpdate.current = true;
      setTasks(prev =>
        prev.map(t => {
          if (t.id !== taskId) return t;
          const updated = { ...t, ...changes };
          if (changes.startDate) updated.startDate = new Date(changes.startDate);
          if (changes.endDate) updated.endDate = new Date(changes.endDate);
          return updated;
        })
      );
      setChartVersion(version);
      isRemoteUpdate.current = false;
    };

    const onTaskDeleted = ({ taskId, version }) => {
      isRemoteUpdate.current = true;
      setTasks(prev => prev.filter(t => t.id !== taskId));
      setChartVersion(version);
      isRemoteUpdate.current = false;
    };

    const onTaskReordered = ({ taskIds, version }) => {
      isRemoteUpdate.current = true;
      setTasks(prev => {
        const taskMap = {};
        prev.forEach(t => { taskMap[t.id] = t; });
        return taskIds.map(id => taskMap[id]).filter(Boolean);
      });
      setChartVersion(version);
      isRemoteUpdate.current = false;
    };

    // Milestone 이벤트
    const onMilestoneAdded = ({ milestone, version }) => {
      isRemoteUpdate.current = true;
      setMilestones(prev => {
        if (prev.find(m => m.id === milestone.id)) return prev;
        return [...prev, { ...milestone, date: new Date(milestone.date) }];
      });
      setChartVersion(version);
      isRemoteUpdate.current = false;
    };

    const onMilestoneUpdated = ({ milestoneId, changes, version }) => {
      isRemoteUpdate.current = true;
      setMilestones(prev =>
        prev.map(m => {
          if (m.id !== milestoneId) return m;
          const updated = { ...m, ...changes };
          if (changes.date) updated.date = new Date(changes.date);
          return updated;
        })
      );
      setChartVersion(version);
      isRemoteUpdate.current = false;
    };

    const onMilestoneDeleted = ({ milestoneId, version }) => {
      isRemoteUpdate.current = true;
      setMilestones(prev => prev.filter(m => m.id !== milestoneId));
      setChartVersion(version);
      isRemoteUpdate.current = false;
    };

    // Member 이벤트
    const onMemberAdded = ({ member, version }) => {
      isRemoteUpdate.current = true;
      setUsers(prev => {
        if (prev.find(u => u.id === member.id)) return prev;
        return [...prev, member];
      });
      setChartVersion(version);
      isRemoteUpdate.current = false;
    };

    const onMemberUpdated = ({ memberId, changes, version }) => {
      isRemoteUpdate.current = true;
      setUsers(prev =>
        prev.map(u => (u.id === memberId ? { ...u, ...changes } : u))
      );
      setChartVersion(version);
      isRemoteUpdate.current = false;
    };

    const onMemberRemoved = ({ memberId, version }) => {
      isRemoteUpdate.current = true;
      setUsers(prev => prev.filter(u => u.id !== memberId));
      setChartVersion(version);
      isRemoteUpdate.current = false;
    };

    // 전체 동기화 수신
    const onSyncState = ({ tasks: syncTasks, milestones: syncMilestones, users: syncUsers, version }) => {
      isRemoteUpdate.current = true;
      if (syncTasks) {
        setTasks(syncTasks.map(t => ({
          ...t,
          startDate: new Date(t.startDate),
          endDate: new Date(t.endDate),
        })));
      }
      if (syncMilestones) {
        setMilestones(syncMilestones.map(m => ({
          ...m,
          date: new Date(m.date),
        })));
      }
      if (syncUsers) {
        setUsers(syncUsers);
      }
      setChartVersion(version);
      isRemoteUpdate.current = false;
    };

    // 충돌 감지
    const onSyncConflict = ({ expectedVersion, actualVersion }) => {
      console.warn(`[Collab] Version conflict: expected ${expectedVersion}, actual ${actualVersion}`);
      requestSync();
    };

    socket.on('task:added', onTaskAdded);
    socket.on('task:updated', onTaskUpdated);
    socket.on('task:deleted', onTaskDeleted);
    socket.on('task:reordered', onTaskReordered);
    socket.on('milestone:added', onMilestoneAdded);
    socket.on('milestone:updated', onMilestoneUpdated);
    socket.on('milestone:deleted', onMilestoneDeleted);
    socket.on('member:added', onMemberAdded);
    socket.on('member:updated', onMemberUpdated);
    socket.on('member:removed', onMemberRemoved);
    socket.on('sync:state', onSyncState);
    socket.on('sync:conflict', onSyncConflict);

    return () => {
      socket.off('task:added', onTaskAdded);
      socket.off('task:updated', onTaskUpdated);
      socket.off('task:deleted', onTaskDeleted);
      socket.off('task:reordered', onTaskReordered);
      socket.off('milestone:added', onMilestoneAdded);
      socket.off('milestone:updated', onMilestoneUpdated);
      socket.off('milestone:deleted', onMilestoneDeleted);
      socket.off('member:added', onMemberAdded);
      socket.off('member:updated', onMemberUpdated);
      socket.off('member:removed', onMemberRemoved);
      socket.off('sync:state', onSyncState);
      socket.off('sync:conflict', onSyncConflict);
    };
  }, [socket, currentChartId, setTasks, setMilestones, setUsers, setChartVersion, requestSync]);

  // ===== 래핑된 상태 변경 함수들 =====

  const collaborativeAddTask = useCallback((task) => {
    setTasks(prev => [...prev, task]);
    if (currentChartId && !isRemoteUpdate.current) {
      const serialized = {
        ...task,
        startDate: task.startDate.toISOString(),
        endDate: task.endDate.toISOString(),
      };
      emitTaskAdd(serialized);
    }
  }, [currentChartId, setTasks, emitTaskAdd]);

  const collaborativeUpdateTask = useCallback((updatedTask) => {
    setTasks(prev => prev.map(t => (t.id === updatedTask.id ? updatedTask : t)));
    if (currentChartId && !isRemoteUpdate.current) {
      const changes = {
        ...updatedTask,
        startDate: updatedTask.startDate.toISOString(),
        endDate: updatedTask.endDate.toISOString(),
      };
      emitTaskUpdate(updatedTask.id, changes);
    }
  }, [currentChartId, setTasks, emitTaskUpdate]);

  const collaborativeDeleteTask = useCallback((taskId) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
    if (currentChartId && !isRemoteUpdate.current) {
      emitTaskDelete(taskId);
    }
  }, [currentChartId, setTasks, emitTaskDelete]);

  const collaborativeReorderTasks = useCallback((reorderedTasks) => {
    setTasks(reorderedTasks);
    if (currentChartId && !isRemoteUpdate.current) {
      emitTaskReorder(reorderedTasks.map(t => t.id));
    }
  }, [currentChartId, setTasks, emitTaskReorder]);

  const collaborativeAddMilestone = useCallback((milestone) => {
    setMilestones(prev => [...prev, milestone]);
    if (currentChartId && !isRemoteUpdate.current) {
      emitMilestoneAdd({
        ...milestone,
        date: milestone.date.toISOString(),
      });
    }
  }, [currentChartId, setMilestones, emitMilestoneAdd]);

  const collaborativeUpdateMilestone = useCallback((updatedMilestone) => {
    setMilestones(prev =>
      prev.map(m => (m.id === updatedMilestone.id ? updatedMilestone : m))
    );
    if (currentChartId && !isRemoteUpdate.current) {
      const changes = {
        ...updatedMilestone,
        date: updatedMilestone.date.toISOString(),
      };
      emitMilestoneUpdate(updatedMilestone.id, changes);
    }
  }, [currentChartId, setMilestones, emitMilestoneUpdate]);

  const collaborativeDeleteMilestone = useCallback((milestoneId) => {
    setMilestones(prev => prev.filter(m => m.id !== milestoneId));
    if (currentChartId && !isRemoteUpdate.current) {
      emitMilestoneDelete(milestoneId);
    }
  }, [currentChartId, setMilestones, emitMilestoneDelete]);

  const collaborativeAddMember = useCallback((member) => {
    setUsers(prev => [...prev, member]);
    if (currentChartId && !isRemoteUpdate.current) {
      emitMemberAdd(member);
    }
  }, [currentChartId, setUsers, emitMemberAdd]);

  const collaborativeUpdateMember = useCallback((memberId, changes) => {
    setUsers(prev =>
      prev.map(u => (u.id === memberId ? { ...u, ...changes } : u))
    );
    if (currentChartId && !isRemoteUpdate.current) {
      emitMemberUpdate(memberId, changes);
    }
  }, [currentChartId, setUsers, emitMemberUpdate]);

  const collaborativeRemoveMember = useCallback((memberId) => {
    setUsers(prev => prev.filter(u => u.id !== memberId));
    if (currentChartId && !isRemoteUpdate.current) {
      emitMemberRemove(memberId);
    }
  }, [currentChartId, setUsers, emitMemberRemove]);

  // 선택 알림
  const selectTask = useCallback((taskId) => {
    emitSelection(taskId);
  }, [emitSelection]);

  return {
    addTask: collaborativeAddTask,
    updateTask: collaborativeUpdateTask,
    deleteTask: collaborativeDeleteTask,
    reorderTasks: collaborativeReorderTasks,
    addMilestone: collaborativeAddMilestone,
    updateMilestone: collaborativeUpdateMilestone,
    deleteMilestone: collaborativeDeleteMilestone,
    addMember: collaborativeAddMember,
    updateMember: collaborativeUpdateMember,
    removeMember: collaborativeRemoveMember,
    selectTask,
    isRemoteUpdate,
  };
}
